import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { apiGet } from './client'
import { acquirePortalLink, type PortalStatus } from './portalSocket'
import { realtimeConfigQuery } from './realtime'

/**
 * Urmarirea live a scanarilor ADM - `Adm::scanTicket` / `Adm::scanState`,
 * `adm.js` (prime / subscribe / SCAN_* ).
 *
 * Trei verigi pot fi rupte - api-ul nu ajunge la socket, socketul ruleaza fara
 * secret, biletul e refuzat - si toate arata la fel de afara: nu vine niciun
 * cadru. De-aia starea se REIA si prin polling la 15 s cat timp socketul tace
 * (`watching` false), iar o scanare disparuta din `state` cat nu vine niciun
 * cadru se trece pe "finished": altfel ar ramane "in curs" pentru totdeauna
 * exact cand socketul e cel care nu merge.
 */

export interface ScanRun {
  run_id: number
  range_id: number | null
  state: number
  total: number
  responded: number
  created: number
  errors: number
  relay: string
  started: string
}

export interface ScanTicket {
  status: 'OK' | 'ERROR'
  ticket?: string
  rooms?: string[]
  message?: string
  reason?: string
}

export function scanTicket(runId = 0): Promise<ScanTicket> {
  return apiGet<ScanTicket>(`/adm/runs/live/ticket${runId > 0 ? `?run=${runId}` : ''}`)
}

export function scanState(): Promise<{ status: string; runs: ScanRun[] }> {
  return apiGet<{ status: string; runs: ScanRun[] }>('/adm/runs/live/state')
}

export const POLL_MS = 15000

export interface ScanLive {
  runs: Record<number, ScanRun>
  /** Vine ceva pe socket? Altfel cifrele sunt de la polling. */
  watching: boolean
  /** Ce a spus serverul cand n-a dat bilet (fara secret, fara firma). */
  note: string
  status: PortalStatus | 'off'
  /** Creste la fiecare cadru sau tur - ecranul reciteste tabelul pe el. */
  frame: number
}

/** `merge()` din adm.js: un cadru peste ce se stia despre scanare. */
export function mergeRun(runs: Record<number, ScanRun>, data: Partial<ScanRun> & { run_id: number }): Record<number, ScanRun> {
  const id = Number(data.run_id)
  const old = runs[id] ?? { run_id: id, range_id: null, state: 1, total: 0, responded: 0, created: 0, errors: 0, relay: '', started: '' }
  return { ...runs, [id]: { ...old, ...data, run_id: id } }
}

/** `prime(polling)`: ce nu mai e in `state` si era in curs s-a incheiat. */
export function primeRuns(runs: Record<number, ScanRun>, fresh: ScanRun[], polling: boolean): Record<number, ScanRun> {
  let next = { ...runs }
  for (const run of fresh) {
    if (polling || !next[run.run_id]) next = mergeRun(next, run)
  }
  if (polling) {
    for (const id of Object.keys(next).map(Number)) {
      const still = fresh.some((r) => r.run_id === id)
      if (!still && (next[id]!.state === 1 || next[id]!.state === 2)) next[id] = { ...next[id]!, state: 3 }
    }
  }
  return next
}

export function useScanLive(runId = 0, enabled = true): ScanLive {
  const config = useQuery({ ...realtimeConfigQuery, enabled }).data
  const [runs, setRuns] = useState<Record<number, ScanRun>>({})
  const [watching, setWatching] = useState(false)
  const [note, setNote] = useState('')
  const [status, setStatus] = useState<PortalStatus | 'off'>('off')
  const [frame, setFrame] = useState(0)

  useEffect(() => {
    if (!enabled) return
    let live = true
    let lastFrame = 0
    const prime = async (polling: boolean) => {
      try {
        const out = await scanState()
        if (!live || out.status !== 'OK') return
        setRuns((cur) => primeRuns(cur, out.runs, polling))
        setFrame((f) => f + 1)
      } catch {
        /* fara stare nu se schimba nimic */
      }
    }
    void prime(false)
    const poll = window.setInterval(() => {
      /* Cat vin cadre pe socket, nu se mai intreaba; cand tac (30 s), da. */
      if (Date.now() - lastFrame > 2 * POLL_MS) {
        setWatching(false)
        void prime(true)
      }
    }, POLL_MS)

    let off: (() => void) | null = null
    let release: (() => void) | null = null
    if (config !== undefined && config.url !== null) {
      const link = acquirePortalLink(config)
      release = () => link.release()
      const subscribe = async () => {
        if (link.status !== 'ready' || off !== null) return
        try {
          const t = await scanTicket(runId)
          if (!live || !t.ticket) {
            if (t.message) setNote(t.message)
            return
          }
          off = link.scanWatch(t.ticket, (event, payload) => {
            const data = (payload ?? {}) as Partial<ScanRun> & { run_id?: number; targets?: unknown }
            if (event === 'SCAN_REFUSED') {
              setNote('The live channel refused the ticket.')
              return
            }
            lastFrame = Date.now()
            setWatching(true)
            if ((event === 'SCAN_PROGRESS' || event === 'SCAN_FINISHED') && data.run_id) {
              setRuns((cur) => mergeRun(cur, { ...data, run_id: Number(data.run_id), ...(event === 'SCAN_FINISHED' ? { state: Number(data.state ?? 3) } : {}) }))
            }
            setFrame((f) => f + 1)
          })
        } catch {
          /* biletul nu s-a dat; ramane polling-ul */
        }
      }
      const unwatch = link.watch((s) => {
        setStatus(s)
        if (s === 'ready') void subscribe()
      })
      /* Starea de acum, in afara randarii, ca sa nu porneasca o redesenare din efect. */
      window.setTimeout(() => {
        if (live) setStatus(link.status)
      }, 0)
      void subscribe()
      const oldRelease = release
      release = () => {
        unwatch()
        oldRelease()
      }
    } else if (config !== undefined) {
      window.setTimeout(() => {
        if (live) setStatus('unconfigured')
      }, 0)
    }

    return () => {
      live = false
      window.clearInterval(poll)
      off?.()
      release?.()
    }
  }, [config, runId, enabled])

  return { runs, watching, note, status, frame }
}
